// react
import { useMemo, useState } from 'react';

// mui
import {
	alpha,
	Box,
	Chip,
	Grid,
	InputAdornment,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import { CalendarMonth as IconCalendar } from '@mui/icons-material';

// utils
import { formatDate } from '../../../utils/dates';

// Types

export interface TimelinePreset {
	id: string;
	label: string;
	startDate: string;
	endDate: string;
}

export interface TimelineDates {
	startDate: string;
	endDate: string;
}

export interface TimelinePresetsProps {
	/**
	 * Currently selected preset ID
	 */
	selectedPreset: string;
	/**
	 * Current start date value
	 */
	startDate: string;
	/**
	 * Current end date value
	 */
	endDate: string;
	/**
	 * Callback when dates change (preset selected or custom dates entered)
	 */
	onChange: (dates: TimelineDates & { presetId: string }) => void;
	/**
	 * Whether dates are required (shows validation errors)
	 * @default false
	 */
	required?: boolean;
	/**
	 * Whether the timeline is optional (shows toggle checkbox)
	 * @default false
	 */
	optional?: boolean;
	/**
	 * Whether to show the timeline (used with optional mode)
	 * @default true
	 */
	isEnabled?: boolean;
	/**
	 * Callback when timeline is toggled (used with optional mode)
	 */
	onToggle?: (enabled: boolean) => void;
	/**
	 * Custom primary presets (overrides default)
	 */
	primaryPresets?: TimelinePreset[];
	/**
	 * Custom extended presets (overrides default)
	 */
	extendedPresets?: TimelinePreset[];
	/**
	 * Whether to include fiscal year presets
	 * @default true
	 */
	includeFiscalYears?: boolean;
	/**
	 * Month when fiscal year starts (0-11, where 0=January)
	 * Defaults to 0 (January = calendar year) if not provided
	 * @default 0
	 */
	fiscalYearStartMonth?: number;
	/**
	 * Start date label
	 * @default "Start Date"
	 */
	startDateLabel?: string;
	/**
	 * End date label
	 * @default "End Date"
	 */
	endDateLabel?: string;
}

// Helper Functions

/**
 * Parse a fiscal year start string (MM-DD format) to get the start month (0-11)
 * Returns 0 (January) if the string is null, undefined, or invalid
 * @param fiscalYearStart - String in "MM-DD" format (e.g., "07-01" for July 1)
 * @returns Month number 0-11 (0=January, 6=July, etc.)
 */
export const parseFiscalYearStartMonth = (fiscalYearStart: string | null | undefined): number => {
	if (!fiscalYearStart) return 0; // Default to January (calendar year)

	const parts = fiscalYearStart.split('-');
	if (parts.length >= 1) {
		const month = parseInt(parts[0], 10);
		if (!isNaN(month) && month >= 1 && month <= 12) {
			return month - 1; // Convert from 1-12 to 0-11
		}
	}
	return 0; // Default to January
};

/**
 * Format a Date as a calendar-date string (YYYY-MM-DD) using its local
 * components. Using toISOString() here would convert to UTC and shift the day
 * for users in timezones west of UTC (e.g. Jan 1 local becomes Dec 31 UTC).
 */
const toCalendarDateString = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Get start and end dates for a specific quarter
 */
export const getQuarterDates = (
	quarter: number,
	year: number
): { startDate: string; endDate: string } => {
	const startMonth = (quarter - 1) * 3;
	const endMonth = startMonth + 2;
	const startDate = new Date(year, startMonth, 1);
	const endDate = new Date(year, endMonth + 1, 0); // Last day of the end month

	return {
		startDate: toCalendarDateString(startDate),
		endDate: toCalendarDateString(endDate),
	};
};

/**
 * Get start and end dates for a fiscal year based on configurable start month
 * @param fy - Fiscal year number (e.g., 2026 for FY2026)
 * @param fyStartMonth - Month when fiscal year starts (0-11, where 0=January). Defaults to 0 (January = calendar year)
 */
export const getFiscalYearDates = (
	fy: number,
	fyStartMonth = 0
): { startDate: string; endDate: string } => {
	let startDate: Date;
	let endDate: Date;

	if (fyStartMonth === 0) {
		// Calendar year: FY2026 = Jan 1, 2026 - Dec 31, 2026
		startDate = new Date(fy, 0, 1);
		endDate = new Date(fy, 11, 31);
	} else {
		// Non-calendar fiscal year: FY starts in previous calendar year
		// e.g., fyStartMonth=6 (July): FY2026 = Jul 1, 2025 - Jun 30, 2026
		startDate = new Date(fy - 1, fyStartMonth, 1);
		endDate = new Date(fy, fyStartMonth, 0); // Last day of month before start month
	}

	return {
		startDate: toCalendarDateString(startDate),
		endDate: toCalendarDateString(endDate),
	};
};

/**
 * Determine the current fiscal year based on current date and fiscal year start month
 * @param currentDate - The current date
 * @param fyStartMonth - Month when fiscal year starts (0-11, where 0=January)
 */
export const getCurrentFiscalYear = (currentDate: Date, fyStartMonth = 0): number => {
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();

	if (fyStartMonth === 0) {
		// Calendar year
		return currentYear;
	} else {
		// If we're past the FY start month, we're in the next FY
		return currentMonth >= fyStartMonth ? currentYear + 1 : currentYear;
	}
};

/**
 * Generate default primary presets based on current date
 * @param currentDate - The current date
 * @param fyStartMonth - Month when fiscal year starts (0-11, where 0=January). Defaults to 0 (January = calendar year)
 */
export const generatePrimaryPresets = (
	currentDate: Date = new Date(),
	fyStartMonth = 0
): TimelinePreset[] => {
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	const currentQuarter = Math.floor(currentMonth / 3) + 1;
	const currentFiscalYear = getCurrentFiscalYear(currentDate, fyStartMonth);

	return [
		{
			id: 'currentQuarter',
			label: `Q${currentQuarter} ${currentYear}`,
			...getQuarterDates(currentQuarter, currentYear),
		},
		{
			id: 'nextQuarter',
			label: currentQuarter < 4 ? `Q${currentQuarter + 1} ${currentYear}` : `Q1 ${currentYear + 1}`,
			...(currentQuarter < 4
				? getQuarterDates(currentQuarter + 1, currentYear)
				: getQuarterDates(1, currentYear + 1)),
		},
		{
			id: 'currentFY',
			label: `FY${currentFiscalYear}`,
			...getFiscalYearDates(currentFiscalYear, fyStartMonth),
		},
		{
			id: 'custom',
			label: 'Custom Range',
			startDate: '',
			endDate: '',
		},
	];
};

/**
 * Generate all extended presets based on current date
 * @param currentDate - The current date
 * @param includeFiscalYears - Whether to include fiscal year presets
 * @param fyStartMonth - Month when fiscal year starts (0-11, where 0=January). Defaults to 0 (January = calendar year)
 */
export const generateExtendedPresets = (
	currentDate: Date = new Date(),
	includeFiscalYears = true,
	fyStartMonth = 0
): TimelinePreset[] => {
	const currentYear = currentDate.getFullYear();

	const presets: TimelinePreset[] = [
		// Current year quarters
		{ id: 'q1ThisYear', label: `Q1 ${currentYear}`, ...getQuarterDates(1, currentYear) },
		{ id: 'q2ThisYear', label: `Q2 ${currentYear}`, ...getQuarterDates(2, currentYear) },
		{ id: 'q3ThisYear', label: `Q3 ${currentYear}`, ...getQuarterDates(3, currentYear) },
		{ id: 'q4ThisYear', label: `Q4 ${currentYear}`, ...getQuarterDates(4, currentYear) },
		// Next year quarters
		{ id: 'q1NextYear', label: `Q1 ${currentYear + 1}`, ...getQuarterDates(1, currentYear + 1) },
		{ id: 'q2NextYear', label: `Q2 ${currentYear + 1}`, ...getQuarterDates(2, currentYear + 1) },
		// Half years
		{
			id: 'h1ThisYear',
			label: `H1 ${currentYear}`,
			startDate: `${currentYear}-01-01`,
			endDate: `${currentYear}-06-30`,
		},
		{
			id: 'h2ThisYear',
			label: `H2 ${currentYear}`,
			startDate: `${currentYear}-07-01`,
			endDate: `${currentYear}-12-31`,
		},
		// Full years
		{
			id: 'thisYear',
			label: `${currentYear}`,
			startDate: `${currentYear}-01-01`,
			endDate: `${currentYear}-12-31`,
		},
		{
			id: 'nextYear',
			label: `${currentYear + 1}`,
			startDate: `${currentYear + 1}-01-01`,
			endDate: `${currentYear + 1}-12-31`,
		},
	];

	// Add fiscal years if requested
	if (includeFiscalYears) {
		const currentFiscalYear = getCurrentFiscalYear(currentDate, fyStartMonth);
		presets.push(
			{
				id: 'currentFY',
				label: `FY${currentFiscalYear}`,
				...getFiscalYearDates(currentFiscalYear, fyStartMonth),
			},
			{
				id: 'nextFY',
				label: `FY${currentFiscalYear + 1}`,
				...getFiscalYearDates(currentFiscalYear + 1, fyStartMonth),
			}
		);
	}

	// Always add custom at the end
	presets.push({ id: 'custom', label: 'Custom Range', startDate: '', endDate: '' });

	return presets;
};

// Component

export const TimelinePresets = ({
	selectedPreset,
	startDate,
	endDate,
	onChange,
	required = false,
	startDateLabel = 'Start Date',
	endDateLabel = 'End Date',
	primaryPresets: customPrimaryPresets,
	extendedPresets: customExtendedPresets,
	includeFiscalYears = true,
	fiscalYearStartMonth = 0,
}: TimelinePresetsProps) => {
	// hooks
	const theme = useTheme();

	// state
	const [showAllPresets, setShowAllPresets] = useState(false);

	// Memoized presets
	const currentDate = useMemo(() => new Date(), []);

	const primaryPresets = useMemo(
		() => customPrimaryPresets || generatePrimaryPresets(currentDate, fiscalYearStartMonth),
		[customPrimaryPresets, currentDate, fiscalYearStartMonth]
	);

	const extendedPresets = useMemo(
		() =>
			customExtendedPresets ||
			generateExtendedPresets(currentDate, includeFiscalYears, fiscalYearStartMonth),
		[customExtendedPresets, currentDate, includeFiscalYears, fiscalYearStartMonth]
	);

	// Determine which presets to show
	const visiblePresets = showAllPresets ? extendedPresets : primaryPresets;

	// handlers
	const handlePresetClick = (preset: TimelinePreset) => {
		if (preset.id === 'custom') {
			onChange({
				presetId: 'custom',
				startDate: startDate,
				endDate: endDate,
			});
		} else {
			onChange({
				presetId: preset.id,
				startDate: preset.startDate,
				endDate: preset.endDate,
			});
		}
	};

	const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange({
			presetId: 'custom',
			startDate: e.target.value,
			endDate: endDate,
		});
	};

	const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange({
			presetId: 'custom',
			startDate: startDate,
			endDate: e.target.value,
		});
	};

	return (
		<Box>
			{/* Timeline Presets */}
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
				{visiblePresets.map((preset) => {
					const isSelected = selectedPreset === preset.id;
					const isCustom = preset.id === 'custom';
					return (
						<Chip
							key={preset.id}
							label={preset.label}
							onClick={() => handlePresetClick(preset)}
							sx={{
								bgcolor: isSelected
									? isCustom
										? alpha(theme.palette.secondary.main, 0.15)
										: alpha(theme.palette.primary.main, 0.15)
									: 'transparent',
								border: '1px solid',
								borderColor: isSelected
									? isCustom
										? theme.palette.secondary.main
										: theme.palette.primary.main
									: 'divider',
								color: isSelected
									? isCustom
										? theme.palette.secondary.dark
										: theme.palette.primary.dark
									: 'text.primary',
								cursor: 'pointer',
								fontWeight: isSelected ? 600 : 400,
								'&:hover': {
									bgcolor: isSelected ? undefined : alpha(theme.palette.primary.main, 0.08),
								},
							}}
							variant={isSelected ? 'filled' : 'outlined'}
						/>
					);
				})}
				{/* More/Less toggle */}
				<Chip
					label={showAllPresets ? 'Less' : 'More...'}
					onClick={() => setShowAllPresets((prev) => !prev)}
					sx={{
						bgcolor: 'transparent',
						border: '1px dashed',
						borderColor: 'divider',
						color: 'text.secondary',
						cursor: 'pointer',
						fontWeight: 400,
						'&:hover': {
							bgcolor: alpha(theme.palette.grey[500], 0.08),
						},
					}}
					variant="outlined"
				/>
			</Box>

			{/* Show selected range summary */}
			{selectedPreset && selectedPreset !== 'custom' && startDate && (
				<Box
					sx={{
						bgcolor: alpha(theme.palette.primary.main, 0.04),
						borderRadius: 1,
						display: 'inline-flex',
						alignItems: 'center',
						gap: 1,
						mb: 2,
						px: 1.5,
						py: 0.75,
					}}>
					<IconCalendar sx={{ color: 'primary.main', fontSize: 16 }} />
					<Typography variant="body2" color="text.secondary">
						{formatDate({ date: startDate })} – {formatDate({ date: endDate })}
					</Typography>
				</Box>
			)}

			{/* Custom Date Range - only show when custom is selected */}
			{selectedPreset === 'custom' && (
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<TextField
							error={required && !startDate}
							fullWidth
							helperText={required && !startDate ? `${startDateLabel} is required` : ''}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconCalendar color="action" />
									</InputAdornment>
								),
							}}
							label={startDateLabel}
							onChange={handleStartDateChange}
							required={required}
							type="date"
							value={startDate}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<TextField
							error={required && !endDate}
							fullWidth
							helperText={required && !endDate ? `${endDateLabel} is required` : ''}
							InputLabelProps={{ shrink: true }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconCalendar color="action" />
									</InputAdornment>
								),
							}}
							label={endDateLabel}
							onChange={handleEndDateChange}
							required={required}
							type="date"
							value={endDate}
						/>
					</Grid>
				</Grid>
			)}

			{/* Validation message when no period selected and required */}
			{required && !selectedPreset && (
				<Typography variant="body2" color="error" sx={{ mt: 1 }}>
					Please select a planning period
				</Typography>
			)}
		</Box>
	);
};
